import React, { useState, useEffect } from 'react';
import './AddMaoDeObraForm.css';
import { apiService } from '../../../services/ApiService';

const AddMaoDeObraForm = ({ obraId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    tipoContratacao: 'direto',
    quantidade: 1,
    funcao: '',
    empresa: '',
    funcionarios: [''],
    observacoes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Atualizar a quantidade de funcionários quando o número mudar
  useEffect(() => {
    const atualizarFuncionarios = () => {
      const qtd = parseInt(formData.quantidade) || 0;
      let novosFuncionarios = [...formData.funcionarios];
      
      // Adicionar campos se a quantidade aumentar
      if (qtd > novosFuncionarios.length) {
        for (let i = novosFuncionarios.length; i < qtd; i++) {
          novosFuncionarios.push('');
        }
      } 
      // Remover campos se a quantidade diminuir
      else if (qtd < novosFuncionarios.length) {
        novosFuncionarios = novosFuncionarios.slice(0, qtd);
      }

      setFormData(prev => ({
        ...prev,
        funcionarios: novosFuncionarios
      }));
    };

    atualizarFuncionarios();
  }, [formData.quantidade]);

  // Manipula mudanças nos campos do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Manipula mudanças nos nomes dos funcionários
  const handleFuncionarioChange = (index, value) => {
    const novosFuncionarios = [...formData.funcionarios];
    novosFuncionarios[index] = value;
    
    setFormData({
      ...formData,
      funcionarios: novosFuncionarios
    });
  };

  // Enviar o formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.funcao) {
      setError('Por favor, informe a função dos funcionários');
      return;
    }
    
    if (formData.tipoContratacao === 'terceirizado' && !formData.empresa) {
      setError('Por favor, informe a empresa terceirizada');
      return;
    }
    
    // Verificar se todos os funcionários têm nome
    const temFuncionarioSemNome = formData.funcionarios.some(nome => !nome.trim());
    if (temFuncionarioSemNome) {
      setError('Por favor, informe o nome de todos os funcionários');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Preparar dados para enviar à API
      const dados = {
        tipoContratacao: formData.tipoContratacao,
        quantidade: parseInt(formData.quantidade),
        funcao: formData.funcao,
        funcionarios: formData.funcionarios,
        observacoes: formData.observacoes
      };
      
      // Adicionar empresa se for terceirizado
      if (formData.tipoContratacao === 'terceirizado') {
        dados.empresa = formData.empresa;
      }
      
      // Enviar para a API
      const response = await apiService.addDiarioObraMedia(
        obraId,
        'maoDeObra',
        dados
      );

      if (response.success) {
        if (onSuccess) {
          onSuccess(response.registro || {
            id: response.id || Date.now().toString(),
            dados,
            data: new Date().toISOString()
          });
        }
        onClose();
      } else {
        setError(response.error || 'Erro ao salvar registro de mão de obra. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao adicionar mão de obra:', error);
      setError('Ocorreu um erro ao enviar os dados. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mao-de-obra-form-container">
      {error && (
        <div className="form-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Tipo de Contratação</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="tipoContratacao"
                value="direto"
                checked={formData.tipoContratacao === 'direto'}
                onChange={handleInputChange}
              />
              Contratação Direta
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="tipoContratacao"
                value="terceirizado"
                checked={formData.tipoContratacao === 'terceirizado'}
                onChange={handleInputChange}
              />
              Terceirizado
            </label>
          </div>
        </div>

        {formData.tipoContratacao === 'terceirizado' && (
          <div className="form-group">
            <label htmlFor="empresa">Empresa Terceirizada*</label>
            <input
              type="text"
              id="empresa"
              name="empresa"
              value={formData.empresa}
              onChange={handleInputChange}
              placeholder="Nome da empresa"
              required
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="funcao">Função*</label>
          <input
            type="text"
            id="funcao"
            name="funcao"
            value={formData.funcao}
            onChange={handleInputChange}
            placeholder="Ex: Pedreiro, Eletricista, Auxiliar..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="quantidade">Quantidade de Funcionários*</label>
          <input
            type="number"
            id="quantidade"
            name="quantidade"
            min="1"
            max="20"
            value={formData.quantidade}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Funcionários*</label>
          <div className="funcionarios-list">
            {formData.funcionarios.map((nome, index) => (
              <div key={index} className="funcionario-item">
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => handleFuncionarioChange(index, e.target.value)}
                  placeholder={`Nome do funcionário ${index + 1}`}
                  required
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="observacoes">Observações</label>
          <textarea
            id="observacoes"
            name="observacoes"
            value={formData.observacoes}
            onChange={handleInputChange}
            placeholder="Observações adicionais"
            rows="3"
          ></textarea>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancelar" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn-salvar"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner-small"></div>
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                <span>Salvar</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMaoDeObraForm; 